/**
 * Focused controls for the menu drill-down. Each Layers & Chart leaf opens ONE
 * of these compact controls inside the menu (breadcrumb back returns to the
 * menu), instead of the whole Layers sheet. They drive the same stores the
 * Layers sheet uses, so the chart updates straight away.
 */
import { AnchorPlacement, AnchorBoundaryShape } from './AnchorControls';
import { DiagnosticsPanel } from '@/diag/DiagnosticsPanel';
import { useEffect, useRef, useState } from 'react';
import { useChartPlotterStore, PRIMARY_TRACK_ID } from '@/store';
import type { OrientationMode } from '@/store/orientation';
import type { TrackPoint } from '@/ownship';
import { providerHasDepthData, NO_DEPTH_ON_SOURCE } from '@/chart-sources';
import { useAisStore, aisStatusLabel } from '@/store/ais';
import { useAlarmsStore } from '@/store/alarms';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { useSafetyStore } from '@/store/safety';
import { useOwnVectorsStore } from '@/store/ownVectors';
import { useEblVrmStore } from '@/store/eblVrm';
import { streamAtPosition } from '@/tides';
import { vectorSourceAvailability } from '@/ownship';
import { useRoutingStore } from '@/store/routing';
import { useNotesStore } from '@/store/notes';
import { usePilotagePlannerStore } from '@/store/pilotagePlanner';
import {
  useUnitsStore,
  formatDepth,
  type DepthUnit,
  type DistanceUnit,
  type SpeedUnit,
  type PositionFormat,
} from '@/store/units';
import { formatPosition, formatLatLon, formatVariation, formatBearing, formatDistance } from '@/nav';
import { useSavedTracksStore } from '@/store/savedTracks';
import { exportTrackToGpx } from '@/routing';
import { useSatelliteStore } from '@/store/satellite';
import { useRangeRingsStore } from '@/store/rangeRings';
import { useSettingsStore } from '@/store/settings';
import { usePositionSourceStore } from '@/store/positionSource';
import type { DeviceGnssStatus } from '@/gnss';
import { useGnssStore } from '@/store/gnss';
import { formatFixAge } from '@/gnss/fixHealth';
import { useEnvStore } from '@/store/environment';
import { useWeatherOverlayStore } from '@/store/weatherOverlay';
import { useShellStore } from '@/store/shell';
import { useWalkthroughStore } from '@/store/walkthrough';
import { useBearingDisplayStore } from '@/store/bearingDisplay';
import { SYMBOL_SETS } from '@/map/style/symbolSets';
import { haptic } from '@/ui/haptics';
import { useAlarmSoundsStore } from '@/store/alarmSounds';
import { ALARM_PRIORITY } from '@/safety/alarmState';
import { toneById, nextToneId } from '@/ui/alarmTones';
import { previewAlarmTone } from '@/ui/alarmSound';
import { useBackgroundServiceStore } from '@/store/backgroundService';
import { ChartProviderPicker } from './ChartProviderPicker';
import { UpdateSettings } from './UpdateSettings';
import { EmptyHint } from './EmptyHint';
import { EMPTY_MARKS, EMPTY_TRACKS } from './emptyHints';

/** A labelled glass toggle row (reuses the Layers-sheet styling). */
function ToggleRow({
  title,
  detail,
  on,
  onClick,
  onLongPress,
}: {
  title: string;
  detail: string;
  on: boolean;
  onClick: () => void;
  /** Optional: a press-and-hold on the row jumps to this overlay's own settings
   *  screen (e.g. AIS → AIS configuration). Rows without one just toggle. */
  onLongPress?: () => void;
}) {
  // Press-and-hold detection, mirroring the boat/orientation long-press pattern:
  // a short tap toggles as normal, a hold fires onLongPress and suppresses the
  // tap that follows so we don't also flip the switch.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longFired = useRef(false);
  const startPress = () => {
    if (!onLongPress) return;
    longFired.current = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      longFired.current = true;
      haptic('commit');
      onLongPress();
    }, 450);
  };
  const cancelPress = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  return (
    <div
      className="acp-layer"
      title={onLongPress ? 'Long-press for settings' : undefined}
      onPointerDown={onLongPress ? startPress : undefined}
      onPointerUp={onLongPress ? cancelPress : undefined}
      onPointerLeave={onLongPress ? cancelPress : undefined}
      onPointerCancel={onLongPress ? cancelPress : undefined}
    >
      <div className="lt">
        <b>{title}</b>
        <span>{detail}</span>
      </div>
      <button
        type="button"
        className={`acp-sw${on ? ' on' : ''}`}
        aria-pressed={on}
        aria-label={title}
        onClick={() => {
          // A long-press just fired on this row; swallow the click it would
          // otherwise turn into so the toggle doesn't flip too.
          if (longFired.current) {
            longFired.current = false;
            return;
          }
          haptic('select');
          onClick();
        }}
      />
    </div>
  );
}

function SymbolSetControl() {
  const symbolSet = useChartPlotterStore((s) => s.symbolSet);
  const setSymbolSet = useChartPlotterStore((s) => s.setSymbolSet);
  const showToast = useShellStore((s) => s.showToast);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        How the vector chart is drawn — our own renderings of the standard symbols.
      </span>
      <div className="picks">
        {SYMBOL_SETS.map((set) => (
          <button
            key={set.id}
            type="button"
            className={symbolSet === set.id ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              setSymbolSet(set.id);
              showToast(`${set.name} symbols`);
            }}
          >
            <b>{set.name}</b>
            <span>{set.blurb}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DeclutterControl() {
  const level = useChartPlotterStore((s) => s.declutterLevel);
  const setLevel = useChartPlotterStore((s) => s.setDeclutterLevel);
  const showToast = useShellStore((s) => s.showToast);
  const opts: { id: 'full' | 'medium' | 'minimal'; name: string; blurb: string; toast: string }[] = [
    { id: 'full', name: 'Full detail', blurb: 'everything the chart carries', toast: 'Full chart detail' },
    {
      id: 'medium',
      name: 'Medium',
      blurb: 'a bit of everything, about half the clutter',
      toast: 'Medium detail',
    },
    { id: 'minimal', name: 'Minimal', blurb: 'the bare safe-navigation picture', toast: 'Minimal detail' },
  ];
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        How much detail the vector chart shows. The base picture (depths, land, coastline, principal
        buoyage) always stays; the level chooses how much rides on top.
      </span>
      <div className="picks">
        {opts.map((o) => (
          <button
            key={o.id}
            type="button"
            className={level === o.id ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              setLevel(o.id);
              showToast(o.toast);
            }}
          >
            <b>{o.name}</b>
            <span>{o.blurb}</span>
          </button>
        ))}
      </div>
      {level === 'medium' && (
        <span className="lead" style={{ marginTop: 4 }}>
          Keeps depth contours but drops the spot soundings, keeps the buoys but drops the light flares,
          and shows only the significant buoys — the minor marks and the extra landmarks, names and
          building detail drop out.
        </span>
      )}
    </div>
  );
}

/**
 * Depth shading — a genuinely dual-purpose control: it changes how the chart draws
 * (Layers) and it is a depth-awareness aid (Safety). It appears in both places on
 * purpose; both point here, driving the same settings store.
 */
function DepthShadingControl() {
  const mode = useSettingsStore((s) => s.depthShadingMode);
  const shallowM = useSettingsStore((s) => s.shadingShallowM);
  const deepM = useSettingsStore((s) => s.shadingDeepM);
  const setSettings = useSettingsStore((s) => s.set);
  const showToast = useShellStore((s) => s.showToast);
  const stepShallow = (d: number) => {
    const v = Math.max(0, Math.round((shallowM + d) * 10) / 10);
    setSettings({ shadingShallowM: v });
    showToast(`Shallow contour ${v.toFixed(0)} m`);
  };
  const stepDeep = (d: number) => {
    const v = Math.max(0, Math.round((deepM + d) * 10) / 10);
    setSettings({ shadingDeepM: v });
    showToast(`Deep contour ${v.toFixed(0)} m`);
  };
  const modeOpts: { id: 'off' | 'safety' | 'graduated'; name: string; blurb: string; toast: string }[] = [
    { id: 'off', name: 'Off', blurb: 'flat sea, no depth tints', toast: 'Depth shading off' },
    { id: 'safety', name: 'Safety contour', blurb: 'two tones, split at the shallow contour', toast: 'Two-tone safety shading' },
    { id: 'graduated', name: 'Graduated', blurb: 'full shallow / mid / deep ramp', toast: 'Graduated depth shading' },
  ];
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        How much the chart colours depth. Off keeps a flat sea; Safety contour is a single two-tone split
        at the shallow contour; Graduated is the full shallow, mid and deep ramp.
      </span>
      <div className="picks">
        {modeOpts.map((o) => (
          <button
            key={o.id}
            type="button"
            className={mode === o.id ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              setSettings({ depthShadingMode: o.id });
              showToast(o.toast);
            }}
          >
            <b>{o.name}</b>
            <span>{o.blurb}</span>
          </button>
        ))}
      </div>
      {mode === 'off' ? (
        <span className="lead" style={{ marginTop: 4 }}>
          The sea draws one flat colour. Depth contour lines and soundings still show; only the coloured
          bands are off.
        </span>
      ) : (
        <span className="lead" style={{ marginTop: 4 }}>
          {mode === 'safety'
            ? 'Water shoaler than the shallow contour takes the shallow tint; everything deeper takes the deep tint. Set the shallow contour for the passage, say 5 m close inshore.'
            : 'Water shoaler than the shallow contour takes the shallow tint; deeper than the deep contour takes the deep tint; in between is the mid shade. Set them for the passage, say 5 and 20 close inshore, 20 and 50 offshore.'}
        </span>
      )}
      {mode !== 'off' && (
      <>
      <div className="acp-layer">
        <div className="lt">
          <b>Shallow contour</b>
          <span>{shallowM.toFixed(0)} m</span>
        </div>
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Shallower contour" onClick={() => stepShallow(-1)}>&minus;</button>
          <button type="button" className="acp-btn" aria-label="Deeper shallow contour" onClick={() => stepShallow(1)}>+</button>
        </span>
      </div>
      <div className="acp-layer">
        <div className="lt">
          <b>Deep contour</b>
          <span>{deepM.toFixed(0)} m</span>
        </div>
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Shallower deep contour" onClick={() => stepDeep(-1)}>&minus;</button>
          <button type="button" className="acp-btn" aria-label="Deeper contour" onClick={() => stepDeep(1)}>+</button>
        </span>
      </div>
      </>
      )}
    </div>
  );
}

function OrientationControl() {
  const orientation = useChartPlotterStore((s) => s.orientation);
  const setOrientation = useChartPlotterStore((s) => s.setOrientation);
  const showToast = useShellStore((s) => s.showToast);
  const pick = (mode: OrientationMode, label: string) => {
    haptic('tick');
    setOrientation(mode);
    showToast(label);
  };
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Which way is up on the chart. The centre button on the chart cycles this too.
      </span>
      <div className="picks">
        <button
          type="button"
          className={orientation === 'north' ? 'on' : ''}
          onClick={() => pick('north', 'North up')}
        >
          <b>North up</b>
          <span>chart fixed, north at the top</span>
        </button>
        <button
          type="button"
          className={orientation === 'head' ? 'on' : ''}
          onClick={() => pick('head', 'Head up')}
        >
          <b>Head up</b>
          <span>rotates to our heading (where the bow points)</span>
        </button>
        <button
          type="button"
          className={orientation === 'course' ? 'on' : ''}
          onClick={() => pick('course', 'Course up')}
        >
          <b>Course up</b>
          <span>rotates to our course over the ground</span>
        </button>
      </div>
    </div>
  );
}

function OverlaysControl({ onNavigate }: { onNavigate?: MenuNavigate }) {
  const groups = useChartPlotterStore((s) => s.groupVisibility);
  // CATZOC is read from the vector ENC's M_QUAL polygons; a raster/tiles source
  // has none, so the overlay would silently draw nothing. Say so instead.
  const hasDepthData = useChartPlotterStore((s) => providerHasDepthData(s.chartProviderId));
  const toggleGroup = useChartPlotterStore((s) => s.toggleGroup);
  const aisEnabled = useAisStore((s) => s.enabled);
  const setAisEnabled = useAisStore((s) => s.setEnabled);
  const satEnabled = useSatelliteStore((s) => s.enabled);
  const setSatEnabled = useSatelliteStore((s) => s.setEnabled);
  // Range rings have their own settings screen (interval, count, spokes), but the
  // on/off is what people reach for alongside the other overlays, so surface just
  // the switch here wired to the same store. A long-press jumps to the full control.
  const ringsEnabled = useRangeRingsStore((s) => s.enabled);
  const setRingsEnabled = useRangeRingsStore((s) => s.setEnabled);
  const setOverlayMode = useEnvStore((s) => s.setOverlayMode);
  // EBL / VRM live under Search & Go To, but people look for their on/off here
  // with the other overlays (feedback: "I actually can't find vrm and ebl
  // anywhere"). Surface the two switches here, wired to the same store, and a
  // long-press opens the full EBL/VRM sheet for setting the bearing and range.
  const eblOn = useEblVrmStore((s) => s.eblOn);
  const setEblOn = useEblVrmStore((s) => s.setEblOn);
  const vrmOn = useEblVrmStore((s) => s.vrmOn);
  const setVrmOn = useEblVrmStore((s) => s.setVrmOn);
  // Own-ship heading / wind / tide vectors reach off the boat like EBL and VRM,
  // so their on/off belongs here with the other overlays too (feedback: "vrm and
  // ebl and vectors could also do with being on the overlays menu"). Wired to the
  // same OwnVectors store the Own-ship vectors control uses, and a long-press
  // jumps to that control for the vector length and the source hints.
  const vectors = useOwnVectorsStore();
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  // Weather is first in the list because it is the one people come to this screen
  // looking for. On turns the overlay up with its panel open; off puts it away.
  const weatherOn = useWeatherOverlayStore((s) => s.on);
  const openWeather = useWeatherOverlayStore((s) => s.open);
  const closeWeather = useWeatherOverlayStore((s) => s.close);
  // Overlays that own a dedicated settings screen: a long-press jumps straight
  // there. Anything not listed here just toggles on tap, as before.
  const jump = (group: string, leaf: string): (() => void) | undefined =>
    onNavigate ? () => onNavigate({ group, leaf }) : undefined;
  return (
    <div className="acp-chartsrc">
      <span className="lead">Extra layers drawn over the chart. Each stays off until we want it.</span>
      <ToggleRow
        title="Weather"
        detail="Forecast over the chart: pick a model, then nudge through the hours"
        on={weatherOn}
        onClick={() => {
          if (weatherOn) {
            closeWeather();
            return;
          }
          openWeather();
          // Close the menu behind it. The overlay opens with its setup panel at
          // the bottom of the screen, and leaving the menu sitting on top of that
          // is how a working button comes to look like a dead one
          // (the same trap as fb-ms1hh1cn-6).
          closeSheet();
        }}
      />
      <ToggleRow
        title="Satellite imagery"
        detail="Photo base under a vector chart, or instead of a raster"
        on={satEnabled}
        onClick={() => setSatEnabled(!satEnabled)}
      />
      <ToggleRow
        title="Tidal streams"
        detail="The training stream arrows (illustrative, not surveyed)"
        on={!!groups['tidal-stream']}
        onClick={() => {
          const next = !groups['tidal-stream'];
          toggleGroup('tidal-stream');
          if (next) setOverlayMode('streams');
        }}
      />
      <ToggleRow
        title="AIS targets"
        detail="Other vessels, drawn as triangles with a course vector"
        on={aisEnabled}
        onClick={() => setAisEnabled(!aisEnabled)}
        onLongPress={jump('boat', 'ais-config')}
      />
      <ToggleRow
        title="Data-quality (CATZOC)"
        detail="Zones of confidence shaded from the cell"
        on={!!groups['catzoc']}
        onClick={() => toggleGroup('catzoc')}
      />
      {!!groups['catzoc'] && !hasDepthData && (
        <span className="lead" style={{ color: '#e0a03a' }}>{NO_DEPTH_ON_SOURCE}</span>
      )}
      <ToggleRow
        title="Traffic schemes (TSS)"
        detail="Lanes with direction arrows, separation zones, deep-water routes"
        on={groups['traffic'] ?? true}
        onClick={() => toggleGroup('traffic')}
      />
      <ToggleRow
        title="Restricted & caution areas"
        detail="Magenta-dashed limits; tap an area for what the restriction is"
        on={groups['restricted-areas'] ?? true}
        onClick={() => toggleGroup('restricted-areas')}
      />
      <ToggleRow
        title="Cables & pipelines"
        detail="Submarine runs, drawn dashed — the reason not to anchor there"
        on={groups['cables'] ?? false}
        onClick={() => toggleGroup('cables')}
      />
      <ToggleRow
        title="Marks"
        detail="Show your saved marks and waypoints on the chart"
        on={groups['marks'] ?? true}
        onClick={() => toggleGroup('marks')}
        onLongPress={jump('wmt', 'marks')}
      />
      <ToggleRow
        title="Tracks"
        detail="Show the boat's track and your saved tracks"
        on={groups['track'] ?? true}
        onClick={() => toggleGroup('track')}
        onLongPress={jump('wmt', 'saved-tracks')}
      />
      <ToggleRow
        title="Range rings"
        detail="Concentric distance rings centred on the boat — hold for interval, count & spokes"
        on={ringsEnabled}
        onClick={() => setRingsEnabled(!ringsEnabled)}
        onLongPress={jump('layers', 'range-rings')}
      />
      <ToggleRow
        title="EBL (bearing line)"
        detail="The swinging bearing line from the boat — hold to set its bearing"
        on={eblOn}
        onClick={() => setEblOn(!eblOn)}
        onLongPress={() => openSheet('eblvrm')}
      />
      <ToggleRow
        title="VRM (range circle)"
        detail="The single range circle from the boat — hold to set its distance"
        on={vrmOn}
        onClick={() => setVrmOn(!vrmOn)}
        onLongPress={() => openSheet('eblvrm')}
      />
      <ToggleRow
        title="Heading vector"
        detail="Which way the bow points through the water — hold for length & source. Needs a heading source."
        on={vectors.heading}
        onClick={() => vectors.setHeading(!vectors.heading)}
        onLongPress={jump('boat', 'own-vector')}
      />
      <ToggleRow
        title="Wind vector"
        detail="A downwind arrow showing where the wind is pushing us — hold for length & source. Needs a wind source."
        on={vectors.wind}
        onClick={() => vectors.setWind(!vectors.wind)}
        onLongPress={jump('boat', 'own-vector')}
      />
      <ToggleRow
        title="Tide vector"
        detail="An arrow the way the tidal stream sets, its length the rate — hold for length & source. Needs tidal stream data."
        on={vectors.tide}
        onClick={() => vectors.setTide(!vectors.tide)}
        onLongPress={jump('boat', 'own-vector')}
      />
    </div>
  );
}

function CurrencyControl() {
  return (
    <div className="acp-chartsrc">
      <span className="lead">Chart updates and Notices to Mariners.</span>
      <p className="acp-menu-note">
        Update checking isn’t wired up yet — it needs the chart backend. Coming soon. For now the
        training charts are fixed, and not for navigation.
      </p>
    </div>
  );
}

function RangeRingsControl() {
  const enabled = useRangeRingsStore((s) => s.enabled);
  const setEnabled = useRangeRingsStore((s) => s.setEnabled);
  const intervalNm = useRangeRingsStore((s) => s.intervalNm);
  const setIntervalNm = useRangeRingsStore((s) => s.setIntervalNm);
  const count = useRangeRingsStore((s) => s.count);
  const setCount = useRangeRingsStore((s) => s.setCount);
  const spokes = useRangeRingsStore((s) => s.spokes);
  const setSpokes = useRangeRingsStore((s) => s.setSpokes);
  const auto = useRangeRingsStore((s) => s.auto);
  const setAuto = useRangeRingsStore((s) => s.setAuto);
  const intervals = [0.1, 0.25, 0.5, 1, 2, 5];
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Concentric distance rings centred on the boat, tracking it as it moves. Ranges are labelled
        in nautical miles.
      </span>
      <ToggleRow
        title="Show range rings"
        detail={
          enabled
            ? auto
              ? `Auto spacing · ${count} ring${count === 1 ? '' : 's'}`
              : `Every ${intervalNm} nm · ${count} ring${count === 1 ? '' : 's'}`
            : 'Off'
        }
        on={enabled}
        onClick={() => setEnabled(!enabled)}
      />
      <span className="lead" style={{ marginTop: 8 }}>
        Ring interval
      </span>
      <div className="picks">
        <button
          type="button"
          className={auto ? 'on' : ''}
          aria-label="Auto ring spacing"
          onClick={() => {
            haptic('tick');
            setAuto(true);
          }}
        >
          <b>Auto</b>
          <span>scales with zoom</span>
        </button>
        {intervals.map((v) => (
          <button
            key={v}
            type="button"
            className={!auto && intervalNm === v ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              setAuto(false);
              setIntervalNm(v);
            }}
          >
            <b>{v} nm</b>
          </button>
        ))}
      </div>
      <div className="acp-layer">
        <div className="lt">
          <b>Number of rings</b>
          <span>{count}</span>
        </div>
        <input
          className="acp-range"
          style={{ flex: 1, minWidth: 0, maxWidth: 150 }}
          type="range"
          min={1}
          max={12}
          step={1}
          value={count}
          aria-label="Number of rings"
          onChange={(e) => { haptic('tick'); setCount(Number(e.target.value)); }}
        />
      </div>
      <ToggleRow
        title="Cardinal spokes"
        detail="N / E / S / W bearing lines from the boat"
        on={spokes}
        onClick={() => setSpokes(!spokes)}
      />
    </div>
  );
}

function SafetyDepthControl() {
  const safetyDepthM = useSettingsStore((s) => s.safetyDepthM);
  const set = useSettingsStore((s) => s.set);
  const showToast = useShellStore((s) => s.showToast);
  const depthUnit = useUnitsStore((s) => s.depth);
  const step = (delta: number) => {
    const v = Math.max(0, Math.round((safetyDepthM + delta) * 10) / 10);
    set({ safetyDepthM: v });
    showToast(`Safety depth ${formatDepth(v, depthUnit)}`);
  };
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Safety depth is a setting, not an alarm. It&rsquo;s the shallowest water we&rsquo;re happy to float in,
        roughly our draught plus the clearance we want under the keel. It does two jobs. Anything shallower
        shades darker on the chart so shoal water stands out, and it&rsquo;s the depth both the grounding and
        depth alarms measure against. Set it here once and the other two follow.
      </span>
      <div className="acp-layer">
        <div className="lt">
          <b>Safety depth</b>
          <span>{formatDepth(safetyDepthM, depthUnit)}</span>
        </div>
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Shallower" onClick={() => step(-0.5)}>−</button>
          <button type="button" className="acp-btn" aria-label="Deeper" onClick={() => step(0.5)}>+</button>
        </span>
      </div>
      <div className="picks">
        {[2, 3, 5, 10].map((v) => (
          <button
            key={v}
            type="button"
            className={Math.abs(safetyDepthM - v) < 1e-6 ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              set({ safetyDepthM: v });
              showToast(`Safety depth ${formatDepth(v, depthUnit)}`);
            }}
          >
            <b>{formatDepth(v, depthUnit)}</b>
          </button>
        ))}
      </div>
    </div>
  );
}

const DEVICE_GNSS_LABEL: Record<DeviceGnssStatus, string> = {
  idle: 'Off',
  requesting: 'Asking for permission…',
  cached: 'Last known position — still looking',
  live: 'Live — receiving fixes',
  nofix: 'Waiting for a fix…',
  denied: 'Permission denied',
  insecure: 'Needs an HTTPS connection',
  unsupported: 'Not supported on this browser',
  error: 'Geolocation error',
};

function GnssSourceControl() {
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);
  const setSourceKind = usePositionSourceStore((s) => s.setSourceKind);
  const sogDampingSec = useSettingsStore((s) => s.sogDampingSec);
  const set = useSettingsStore((s) => s.set);
  const deviceStatus = usePositionSourceStore((s) => s.deviceStatus);
  const deviceMessage = usePositionSourceStore((s) => s.deviceMessage);
  const fixHealth = usePositionSourceStore((s) => s.fixHealth);
  const fixAgeSec = usePositionSourceStore((s) => s.fixAgeSec);
  const lastFix = useGnssStore((s) => s.lastFix);
  const fixCount = useGnssStore((s) => s.fixCount);
  const showToast = useShellStore((s) => s.showToast);
  const pick = (kind: 'sim' | 'device') => {
    if (kind === sourceKind) return;
    haptic('tick');
    setSourceKind(kind);
    showToast(kind === 'sim' ? 'Simulated position' : 'Using device GNSS');
  };
  // Only truly-real fields are shown. Accuracy is the receiver's own reported
  // horizontal error (a real number on the device feed; the training model's
  // figure on the simulator). We deliberately do NOT show a satellites-used
  // count or HDOP: the device position feed doesn't report them, so any number
  // here would be invented.
  const accM = lastFix?.horizontalErrorM;
  const accText =
    accM != null ? `\u00b1${accM < 10 ? accM.toFixed(1) : Math.round(accM)} m` : 'waiting for a fix';
  const ageText = fixAgeSec != null ? formatFixAge(fixAgeSec * 1000) : null;
  const lastFixText =
    fixCount === 0
      ? 'waiting for the first fix'
      : fixHealth === 'stale' || fixHealth === 'lost'
        ? (ageText ?? 'a while ago')
        : 'live, about a second ago';
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Where our position comes from. Simulated drives the training boat over the practice area;
        Device GNSS uses this device&rsquo;s own receiver for a real fix.
      </span>
      <div className="picks">
        <button type="button" className={sourceKind === 'sim' ? 'on' : ''} onClick={() => pick('sim')}>
          <b>Simulated</b>
          <span>the training boat</span>
        </button>
        <button type="button" className={sourceKind === 'device' ? 'on' : ''} onClick={() => pick('device')}>
          <b>Device GNSS</b>
          <span>this device&rsquo;s GNSS</span>
        </button>
      </div>
      {sourceKind === 'device' && (
        <>
          <p className="acp-menu-note">Device GNSS: {deviceMessage || DEVICE_GNSS_LABEL[deviceStatus]}</p>
          {(deviceStatus === 'insecure' || deviceStatus === 'denied' || deviceStatus === 'unsupported' || deviceStatus === 'error') && (
            <p className="acp-menu-note">
              {deviceStatus === 'insecure'
                ? 'The browser only gives GNSS over a secure connection. Open the app over https:// (localhost counts); over a plain http LAN address it is blocked.'
                : deviceStatus === 'denied'
                  ? 'Location permission was refused. Allow location for this site in the browser, then reselect Device GNSS.'
                  : 'This browser will not provide a device fix here.'}
            </p>
          )}
          {(deviceStatus === 'requesting' || deviceStatus === 'nofix' || deviceStatus === 'live') && (
            <p className="acp-menu-note">
              The boat stays at its last position until the first real fix arrives; then it jumps to where you actually are.
            </p>
          )}
        </>
      )}
      <div className="acp-gnss-status" role="status">
        <div className="row">
          <span>Source</span>
          <span>{sourceKind === 'sim' ? 'Simulated training boat' : 'Device GNSS'}</span>
        </div>
        <div className="row">
          <span>Fix accuracy</span>
          <span>{accText}</span>
        </div>
        {sourceKind === 'device' && (
          <div className="row">
            <span>Last fix</span>
            <span>{lastFixText}</span>
          </div>
        )}
      </div>
      <label className="acp-fieldlabel" style={{ marginTop: 8 }}>SOG smoothing</label>
      <div className="acp-seg">
        {([
          { v: 0, label: 'Off' },
          { v: 5, label: 'Gentle · 5 s' },
          { v: 15, label: 'Steady · 15 s' },
        ] as const).map((o) => (
          <button
            key={o.v}
            type="button"
            className={sogDampingSec === o.v ? 'on' : ''}
            onClick={() => { haptic('tick'); set({ sogDampingSec: o.v }); }}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="acp-menu-note">
        Phone GNSS speed jumps about from fix to fix, so the SOG readout can flick between 4.8 and 5.6 while
        the boat does nothing of the kind. Smoothing averages the last few seconds: Gentle settles the
        flicker, Steady gives a calm number for passage timing at the cost of reacting a touch later. The
        spoofing and jump alarms always see the raw feed.
      </p>
      <p className="acp-menu-note">
        That&rsquo;s what the position source gives us today: where we are and how good the fix is. A
        full sky view, with each satellite&rsquo;s signal strength and which system it&rsquo;s on (GPS,
        GLONASS, Galileo, or BeiDou), needs deeper access to the phone&rsquo;s GNSS chip than the
        position feed hands over, so it&rsquo;s a planned enhancement rather than something we can show
        yet. We leave the satellites-used count off here because the current source doesn&rsquo;t report
        one, and a made-up number would be worse than none.
      </p>
      <p className="acp-menu-note">
        For now it&rsquo;s Simulated or this device&rsquo;s own GNSS. A fuller data-source setup (NMEA
        0183/2000, USB, with primary and backup sources) will join this list later.
      </p>
    </div>
  );
}

function PaletteControl() {
  const palette = useChartPlotterStore((s) => s.palette);
  const setPalette = useChartPlotterStore((s) => s.setPalette);
  const paletteAuto = useChartPlotterStore((s) => s.paletteAuto);
  const setPaletteAuto = useChartPlotterStore((s) => s.setPaletteAuto);
  const hasFix = useChartPlotterStore((s) => s.ownship.position != null);
  const showToast = useShellStore((s) => s.showToast);
  const opts: { id: 'day' | 'dusk' | 'night'; name: string; blurb: string }[] = [
    { id: 'day', name: 'Day', blurb: 'bright, for daylight' },
    { id: 'dusk', name: 'Dusk', blurb: 'dimmed, for twilight' },
    { id: 'night', name: 'Night', blurb: 'red-shifted, keeps night vision' },
  ];
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        The chart colour scheme. Night is red-shifted so it doesn&rsquo;t wreck our night vision on a dark passage.
      </span>
      <div className="picks">
        <button
          type="button"
          className={paletteAuto ? 'on' : ''}
          onClick={() => {
            haptic('tick');
            setPaletteAuto(true);
            showToast('Auto palette — follows the sun');
          }}
        >
          <b>Auto</b>
          <span>day, dusk and night by the sun at our position</span>
        </button>
        {opts.map((o) => (
          <button
            key={o.id}
            type="button"
            className={!paletteAuto && palette === o.id ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              // A manual pick pins the palette: turn Auto off, then set it.
              setPaletteAuto(false);
              setPalette(o.id);
              showToast(`${o.name} palette`);
            }}
          >
            <b>{o.name}</b>
            <span>{o.blurb}</span>
          </button>
        ))}
      </div>
      {paletteAuto && (
        <span className="lead" style={{ marginTop: 4 }}>
          {hasFix
            ? `Following the sun — on ${palette} now. Pick a scheme above to pin it.`
            : 'Waiting for a position fix — the palette stays put until we have one.'}
        </span>
      )}
      {palette === 'night' && <NightBrightnessSlider />}
    </div>
  );
}

function NightBrightnessSlider() {
  const nightBrightness = useSettingsStore((s) => s.nightBrightness);
  const setSettings = useSettingsStore((s) => s.set);
  return (
    <>
      <label className="acp-fieldlabel" style={{ marginTop: 8 }}>
        Night brightness — {Math.round(nightBrightness * 100)}%
      </label>
      <input
        className="acp-range"
        type="range"
        min={0.2}
        max={1}
        step={0.05}
        value={nightBrightness}
        aria-label="Night brightness"
        onChange={(e) => setSettings({ nightBrightness: Number(e.target.value) })}
      />
      <span className="lead" style={{ marginTop: 4 }}>
        Dims the whole screen on a dark passage, so nothing glares and wrecks our night vision. Slide
        left for darker.
      </span>
    </>
  );
}

function HapticsControl() {
  const haptics = useSettingsStore((s) => s.haptics);
  const setSettings = useSettingsStore((s) => s.set);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        A subtle buzz on taps and a firmer one on alarms. It does nothing on a device that can&rsquo;t vibrate,
        so it&rsquo;s safe to leave on.
      </span>
      <ToggleRow
        title="Haptic feedback"
        detail="Buzz on taps and alarms"
        on={haptics}
        onClick={() => setSettings({ haptics: !haptics })}
      />
    </div>
  );
}

function AlarmSoundControl() {
  const alarmSound = useSettingsStore((s) => s.alarmSound);
  const setSettings = useSettingsStore((s) => s.set);
  const soundConfig = useAlarmSoundsStore((s) => s.config);
  const setSound = useAlarmSoundsStore((s) => s.setSound);
  const resetSounds = useAlarmSoundsStore((s) => s.reset);
  const showToast = useShellStore((s) => s.showToast);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        A siren that repeats while a safety alarm is standing (anchor dragging, a close AIS target, shallow
        water and the rest), so it carries across the cockpit rather than sitting silent on screen. Each alarm
        can have its own sound and volume below, so we can tell them apart by ear &mdash; the dangerous ones
        are loud and distinct by default. It does nothing on a device with no audio, so it&rsquo;s safe to
        leave on. When the phone is locked the app can&rsquo;t make this noise, so a loud notification sound
        covers that.
      </span>
      <ToggleRow
        title="Alarm sound"
        detail="Master switch for the siren"
        on={alarmSound}
        onClick={() => setSettings({ alarmSound: !alarmSound })}
      />
      <div className="acp-fieldlabel" style={{ marginTop: 10 }}>
        Sound &amp; volume per alarm
      </div>
      {ALARM_PRIORITY.map((a) => {
        const setting = soundConfig[a.key];
        const tone = toneById(setting.tone);
        return (
          <div key={String(a.key)} className="acp-layer" style={{ display: 'block' }}>
            <div className="lt" style={{ marginBottom: 6 }}>
              <b>{a.label}</b>
              <span>
                {tone.label} &middot; {tone.blurb}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                className="acp-btn"
                aria-label={`Change sound for ${a.label} (currently ${tone.label})`}
                onClick={() => {
                  const next = nextToneId(setting.tone);
                  haptic('tick');
                  setSound(a.key, { tone: next });
                  previewAlarmTone({ tone: next, volume: setting.volume });
                }}
              >
                {tone.label}
              </button>
              <button
                type="button"
                className="acp-btn"
                aria-label={`Test ${a.label} sound`}
                onClick={() => {
                  haptic('tick');
                  previewAlarmTone({ tone: setting.tone, volume: setting.volume });
                }}
              >
                &#9654; Test
              </button>
              <input
                className="acp-range"
                style={{ flex: 1 }}
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={setting.volume}
                aria-label={`${a.label} volume`}
                onChange={(e) => setSound(a.key, { volume: Number(e.target.value) })}
              />
              <span className="acp-fieldlabel" style={{ minWidth: 40, textAlign: 'right' }}>
                {Math.round(setting.volume * 100)}%
              </span>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        className="acp-btn"
        style={{ marginTop: 8 }}
        aria-label="Reset alarm sounds to defaults"
        onClick={() => {
          haptic('tick');
          resetSounds();
          showToast('Alarm sounds reset');
        }}
      >
        Reset to defaults
      </button>
    </div>
  );
}

function BackgroundGpsControl() {
  const manualOverride = useBackgroundServiceStore((s) => s.manualOverride);
  const setManualOverride = useBackgroundServiceStore((s) => s.setManualOverride);
  const serviceRunning = useBackgroundServiceStore((s) => s.serviceRunning);
  const status = useBackgroundServiceStore((s) => s.status);
  const degraded = useBackgroundServiceStore((s) => s.degraded);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Android freezes the app when the screen locks, which stops GNSS, so alarms go quiet and the track
        gets gaps. Turn this on and the installed app runs a small background service (with a notification you
        can see) that keeps GNSS, your armed alarms and track recording alive with the screen off. It starts on
        its own whenever the anchor watch or a safety alarm is armed, so most of the time you don&rsquo;t need
        this switch. Use it to force the service on for a passage even before an alarm is set.
      </span>
      <ToggleRow
        title="Keep GNSS alive in the background"
        detail={
          serviceRunning
            ? degraded
              ? 'Running foreground-only, permission needed'
              : `Background watch running (${status})`
            : 'Starts automatically when an alarm is armed'
        }
        on={manualOverride}
        onClick={() => setManualOverride(!manualOverride)}
      />
      <p className="acp-menu-note">
        Needs the installed Android app, Device GNSS selected, and &ldquo;Allow all the time&rdquo; location.
      </p>
    </div>
  );
}

function GlassControl() {
  const glass = useSettingsStore((s) => s.glassTransparency);
  const setSettings = useSettingsStore((s) => s.set);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        How see-through the menus and sheets are. Slide right for more glass and more chart showing through,
        left for a more solid panel. It updates live and stays legible at both ends.
      </span>
      <label className="acp-fieldlabel" style={{ marginTop: 6 }}>
        Transparency — {Math.round(glass * 100)}%
      </label>
      <input
        className="acp-range"
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={glass}
        aria-label="Glass transparency"
        onChange={(e) => setSettings({ glassTransparency: Number(e.target.value) })}
      />
    </div>
  );
}

function KeepAwakeControl() {
  const keepScreenAwake = useSettingsStore((s) => s.keepScreenAwake);
  const setSettings = useSettingsStore((s) => s.set);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Stops the phone dimming and sleeping while we&rsquo;re watching the chart but not touching it, so a
        pilotage or a close-quarters passage doesn&rsquo;t go dark on us at the wrong moment. It holds the
        screen on only while the app is in front, and it costs battery, so leave it off unless we need it.
      </span>
      <ToggleRow
        title="Keep screen on"
        detail="Hold the display awake during pilotage (uses more battery)"
        on={keepScreenAwake}
        onClick={() => setSettings({ keepScreenAwake: !keepScreenAwake })}
      />
    </div>
  );
}

function AboutControl() {
  const resetSettings = useSettingsStore((s) => s.reset);
  const showToast = useShellStore((s) => s.showToast);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const requestReplay = useWalkthroughStore((s) => s.requestReplay);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        The app version and how to check for updates. These are the fictional RYA training charts, fixed and
        not for navigation, so there&rsquo;s no live chart currency to report yet.
      </span>
      <UpdateSettings />
      <p className="acp-menu-note" style={{ marginTop: 10 }}>
        New here, or fancy a refresher? Run the quick tour again and we&rsquo;ll point out the chart, the MOB
        button, the menu, and your shortcuts.
      </p>
      <button
        type="button"
        className="acp-btn"
        style={{ marginTop: 6 }}
        onClick={() => {
          haptic('tick');
          // Close the menu so the tour has the chart to point at, then replay.
          closeSheet();
          requestReplay();
        }}
      >
        Show the tour again
      </button>
      <p className="acp-menu-note" style={{ marginTop: 10 }}>
        Reset puts our boat figures and chart preferences back to their defaults. It doesn&rsquo;t touch saved
        routes, marks, or tracks.
      </p>
      <button
        type="button"
        className="acp-btn"
        style={{ marginTop: 6 }}
        onClick={() => {
          haptic('tick');
          resetSettings();
          showToast('Settings reset to defaults');
        }}
      >
        Reset to defaults
      </button>
    </div>
  );
}

/** A compact number field for the boat-profile control (commits valid input live). */
function NumField({
  label,
  hint,
  value,
  placeholder,
  min = 0,
  allowEmpty = false,
  onCommit,
}: {
  label: string;
  hint?: string;
  value: number | null;
  placeholder?: string;
  min?: number;
  allowEmpty?: boolean;
  onCommit: (v: number | null) => void;
}) {
  const [t, setT] = useState(value == null ? '' : String(value));
  useEffect(() => setT(value == null ? '' : String(value)), [value]);
  const commit = (v: string) => {
    setT(v);
    const str = v.trim();
    if (str === '') {
      if (allowEmpty) onCommit(null);
      return;
    }
    const n = parseFloat(str);
    if (Number.isFinite(n) && n >= min) onCommit(n);
  };
  return (
    <>
      <label className="acp-fieldlabel">{label}</label>
      <input
        className="acp-text-input"
        value={t}
        inputMode="decimal"
        placeholder={placeholder}
        onChange={(e) => commit(e.target.value)}
        aria-label={label}
      />
      {hint && <p className="acp-menu-note" style={{ marginTop: 2 }}>{hint}</p>}
    </>
  );
}

function BoatProfileControl() {
  const set = useSettingsStore((s) => s.set);
  const lengthM = useSettingsStore((s) => s.lengthM);
  const beamM = useSettingsStore((s) => s.beamM);
  const gnssFromBowM = useSettingsStore((s) => s.gnssFromBowM);
  const gnssFromCentreM = useSettingsStore((s) => s.gnssFromCentreM);
  const draughtM = useSettingsStore((s) => s.draughtM);
  const airDraughtM = useSettingsStore((s) => s.airDraughtM);
  const cruiseSpeedKn = useSettingsStore((s) => s.cruiseSpeedKn);
  const planSpeedRef = useSettingsStore((s) => s.planSpeedRef);
  const speedOpts: { id: 'sog' | 'stw'; name: string; blurb: string }[] = [
    { id: 'sog', name: 'Over ground (SOG)', blurb: 'tide already in it, ETAs are distance ÷ speed' },
    { id: 'stw', name: 'Through water (STW)', blurb: 'we fold the tidal stream into each leg for you' },
  ];
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Our boat&rsquo;s figures, kept on this device. Set them once and every feature that needs them reads
        from here, so there&rsquo;s only one place to keep right.
      </span>
      <NumField
        label="Length overall (m)"
        value={lengthM}
        allowEmpty
        placeholder="e.g. 11"
        onCommit={(v) => set({ lengthM: v })}
        hint="For display and own-ship scale. Leave blank if we're not sure."
      />
      <NumField
        label="Beam (m)"
        value={beamM}
        allowEmpty
        placeholder="e.g. 3.6"
        onCommit={(v) => set({ beamM: v })}
        hint="Length and beam are all we need to draw the boat at its real size once we're zoomed right in. The aerial position below then puts that shape in exactly the right place."
      />
      <NumField
        label="GNSS aerial: distance aft of the bow (m)"
        value={gnssFromBowM}
        allowEmpty
        placeholder="e.g. 9"
        onCommit={(v) => set({ gnssFromBowM: v })}
        hint="The fix is the aerial's position, not the boat's. Leave this blank and we assume it sits amidships. Fill it in and the anchor drops where it really leaves the bow roller, rather than several metres aft of it."
      />
      <NumField
        label="GNSS aerial: offset from the centreline (m)"
        value={gnssFromCentreM}
        placeholder="0"
        onCommit={(v) => set({ gnssFromCentreM: v ?? 0 })}
        hint="Positive to starboard, negative to port. Usually 0. Only worth setting if the aerial is well off to one side."
      />
      <NumField
        label="Draught (m)"
        value={draughtM}
        placeholder="1.5"
        onCommit={(v) => set({ draughtM: v ?? draughtM })}
        hint="The depth alarm uses this to work out the clearance under the keel."
      />
      <NumField
        label="Air draught (m)"
        value={airDraughtM}
        allowEmpty
        placeholder="e.g. 14"
        onCommit={(v) => set({ airDraughtM: v })}
        hint="Height of our highest fixed point above the water. Bridge clearance checks against this."
      />
      <NumField
        label="Cruising speed (kn)"
        value={cruiseSpeedKn}
        placeholder="5"
        onCommit={(v) => set({ cruiseSpeedKn: v ?? cruiseSpeedKn })}
        hint="Used for ETA when we're stopped, and as the passage-planning speed."
      />
      <span className="lead" style={{ marginTop: 6 }}>
        Is that speed over the ground or through the water? If it&rsquo;s through the water we&rsquo;ll
        check the tidal streams so the route ETAs come out right.
      </span>
      <div className="picks">
        {speedOpts.map((o) => (
          <button
            key={o.id}
            type="button"
            className={planSpeedRef === o.id ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              set({ planSpeedRef: o.id });
            }}
          >
            <b>{o.name}</b>
            <span>{o.blurb}</span>
          </button>
        ))}
      </div>
      {/* Safety depth and bridge clearance are boat-characteristic figures too
          (draught and air-draught related), so we let them be set right here as
          well as from the Safety menu. These reuse the very same store-backed
          controls, so a change in either place shows up in both. */}
      <span className="lead" style={{ marginTop: 10 }}>
        Two more boat figures we can set here as well as from the Safety menu. They&rsquo;re the same
        settings wherever we open them, so they stay in step.
      </span>
      <SafetyDepthControl />
      <BridgeClearanceControl hideAirDraught />
    </div>
  );
}

function BearingDisplayControl() {
  const mode = useBearingDisplayStore((s) => s.mode);
  const variationDeg = useBearingDisplayStore((s) => s.variationDeg);
  const variationEast = useBearingDisplayStore((s) => s.variationEast);
  const auto = useBearingDisplayStore((s) => s.auto);
  const autoVariationDeg = useBearingDisplayStore((s) => s.autoVariationDeg);
  const effectiveVariationDeg = useBearingDisplayStore((s) => s.effectiveVariationDeg);
  const setMode = useBearingDisplayStore((s) => s.setMode);
  const setManualVariation = useBearingDisplayStore((s) => s.setManualVariation);
  const setAuto = useBearingDisplayStore((s) => s.setAuto);
  const magnetic = mode === 'magnetic';
  // The box holds the magnitude only; the dropdown carries the hemisphere. We
  // recombine them into the signed, East-positive value the formatter wants.
  const magnitude = Math.abs(variationDeg);
  // Auto is on but with no fix yet, so we've fallen back to the manual value.
  const autoNoFix = auto && autoVariationDeg == null;
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Which north our bearings are measured from. True points at the geographic pole and is how the chart and
        our plotting work; Magnetic is what a hand or steering compass reads, once the local variation is taken
        off. Every bearing on screen carries a &deg;T or &deg;M so it&rsquo;s always clear which one we&rsquo;re looking at.
      </span>
      <div className="picks">
        <button type="button" className={!magnetic ? 'on' : ''} onClick={() => { haptic('tick'); setMode('true'); }}>
          <b>True (&deg;T)</b>
          <span>geographic north, matches the chart</span>
        </button>
        <button type="button" className={magnetic ? 'on' : ''} onClick={() => { haptic('tick'); setMode('magnetic'); }}>
          <b>Magnetic (&deg;M)</b>
          <span>what the compass reads</span>
        </button>
      </div>
      {magnetic && (
        <>
          <label className="acp-fieldlabel">Where the variation comes from</label>
          <div className="picks">
            <button type="button" className={auto ? 'on' : ''} onClick={() => { haptic('tick'); setAuto(true); }}>
              <b>Auto (WMM)</b>
              <span>worked out from our position</span>
            </button>
            <button type="button" className={!auto ? 'on' : ''} onClick={() => { haptic('tick'); setAuto(false); }}>
              <b>Manual</b>
              <span>we type it in</span>
            </button>
          </div>
          {(!auto || autoNoFix) && (
            <>
              <label className="acp-fieldlabel">Magnetic variation</label>
              <div className="acp-var-entry">
                <input
                  className="acp-text-input"
                  value={magnitude === 0 ? '' : String(magnitude)}
                  inputMode="decimal"
                  placeholder="0"
                  aria-label="Variation, degrees"
                  onChange={(e) => {
                    const n = parseFloat(e.target.value.trim());
                    setManualVariation(Number.isFinite(n) && n >= 0 ? n : 0, variationEast);
                  }}
                />
                <span className="acp-var-unit">&deg;</span>
                <select
                  value={variationEast ? 'E' : 'W'}
                  aria-label="Variation East or West"
                  onChange={(e) => { haptic('tick'); setManualVariation(magnitude, e.target.value === 'E'); }}
                >
                  <option value="W">West</option>
                  <option value="E">East</option>
                </select>
              </div>
              <p className="acp-menu-note" style={{ marginTop: 2 }}>
                Read it off the chart&rsquo;s compass rose for our area. West variation makes the magnetic bearing
                bigger than true, East makes it smaller, so we take the variation off a true bearing to get the
                &deg;M one.
              </p>
            </>
          )}
          <p className="acp-menu-note" style={{ marginTop: 2 }}>
            Variation now{' '}
            <b>
              {formatVariation(effectiveVariationDeg)}
              {auto && !autoNoFix ? ' (auto, WMM)' : ''}
            </b>
            {autoNoFix
              ? '. No position fix yet, so we\u2019re using the manual value until the GNSS locks on.'
              : auto
                ? '. This follows the World Magnetic Model at the boat, and updates as we move.'
                : '.'}
          </p>
        </>
      )}
      <p className="acp-menu-note">
        We work in true under the bonnet and only convert for display, so switching back and forth never changes
        the plot, just how the numbers read.
      </p>
    </div>
  );
}

function OwnShipVectorControl() {
  const ownVectorMin = useSettingsStore((s) => s.ownVectorMin);
  const set = useSettingsStore((s) => s.set);
  // EBL / VRM are the other two lines that reach off the boat, so people expect
  // their on/off switches alongside the vectors (feedback: "On the vectors menu
  // we should probably have the ability to turn vrm and ebl on and off"). Same
  // store as the sheet, so the chart follows straight away.
  const eblOn = useEblVrmStore((s) => s.eblOn);
  const setEblOn = useEblVrmStore((s) => s.setEblOn);
  const eblBearingDeg = useEblVrmStore((s) => s.eblBearingDeg);
  const setEblBearingDeg = useEblVrmStore((s) => s.setEblBearingDeg);
  const nudgeEbl = useEblVrmStore((s) => s.nudgeEbl);
  const vrmOn = useEblVrmStore((s) => s.vrmOn);
  const setVrmOn = useEblVrmStore((s) => s.setVrmOn);
  const vrmRangeNm = useEblVrmStore((s) => s.vrmRangeNm);
  const setVrmRangeNm = useEblVrmStore((s) => s.setVrmRangeNm);
  const nudgeVrm = useEblVrmStore((s) => s.nudgeVrm);
  const openSheet = useShellStore((s) => s.openSheet);
  // Range rings are the other set of circles off the boat, so their on/off sits
  // here too, wired to the same store. The interval, count and spokes live on the
  // dedicated Range rings control, a tap away under Layers.
  const ringsEnabled = useRangeRingsStore((s) => s.enabled);
  const setRingsEnabled = useRangeRingsStore((s) => s.setEnabled);
  const vrmRanges = [0.25, 0.5, 1, 2, 5, 10];
  const presets = [6, 30, 60];
  // "Custom" lets us type any minutes value. It shows active whenever the stored
  // value isn't one of the presets, or when the user taps Custom.
  const [customVector, setCustomVector] = useState(!presets.includes(ownVectorMin));

  // Which of the four vectors to draw, plus the shortcut to the right source when
  // one is on but has nothing feeding it.
  const vectors = useOwnVectorsStore();
  const openMenuAt = useShellStore((s) => s.openMenuAt);
  const position = useChartPlotterStore((s) => s.ownship.position);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);
  // Tide has a source only where there's stream data; sample the rate at the boat
  // so the prompt is honest for where we actually are.
  const tideRateKn = position ? streamAtPosition(position, streamTimeMs).rateKn : 0;
  const available = vectorSourceAvailability({ hasFix: position != null, sourceKind, tideRateKn });

  const rows: {
    id: 'cogsog' | 'heading' | 'wind' | 'tide';
    title: string;
    detail: string;
    on: boolean;
    setOn: (b: boolean) => void;
    missing: string;
    where: string;
    group: string;
    leaf: string;
  }[] = [
    {
      id: 'cogsog',
      title: 'Course & speed (COG/SOG)',
      detail: 'The ground vector: the way we are actually made good over the ground, straight off the fix.',
      on: vectors.cogsog,
      setOn: vectors.setCogsog,
      missing:
        'No position fix yet. Once the GNSS or the simulator has a fix, this one draws on its own, nothing to connect.',
      where: 'Sensors & sources',
      group: 'boat',
      leaf: 'sensors',
    },
    {
      id: 'heading',
      title: 'Heading (water track)',
      detail: 'Which way the bow points through the water. Needs a heading source.',
      on: vectors.heading,
      setOn: vectors.setHeading,
      missing:
        'No heading source connected. A phone GNSS gives us course over the ground but not which way the bow points, so there is nothing to draw a water track from yet. Connect a heading source and it will show.',
      where: 'Sensors & sources',
      group: 'boat',
      leaf: 'sensors',
    },
    {
      id: 'wind',
      title: 'Wind',
      detail: 'A downwind arrow showing where the wind is pushing us. Needs a wind source.',
      on: vectors.wind,
      setOn: vectors.setWind,
      missing:
        'No wind source connected. We need wind data to draw where the wind is setting us, and there is no wind input hooked up yet.',
      where: 'Sensors & sources',
      group: 'boat',
      leaf: 'sensors',
    },
    {
      id: 'tide',
      title: 'Tide (set & rate)',
      detail: 'An arrow the way the tidal stream sets, its length the rate. Needs tidal stream data.',
      on: vectors.tide,
      setOn: vectors.setTide,
      missing:
        'No tidal stream data for where we are. The set-and-rate arrow reads the stream near us, which we have around the charted tidal diamonds. Check the tidal stream source.',
      where: 'Tidal streams',
      group: 'layers',
      leaf: 'overlays',
    },
  ];

  const ownshipIcon = useSettingsStore((s) => s.ownshipIcon);
  const ownVectorColour = useSettingsStore((s) => s.ownVectorColour);
  const windGreenFromKn = useSettingsStore((s) => s.windGreenFromKn);
  const windRedFromKn = useSettingsStore((s) => s.windRedFromKn);

  return (
    <div className="acp-chartsrc">
      <div className="rgn">Boat symbol</div>
      <span className="lead">
        Which symbol marks our own boat. The Ardent arrow is the mark from the logo; the plain triangle is the
        simpler shape if that reads more clearly for us.
      </span>
      <div className="picks">
        {([
          { id: 'ardent', name: 'Ardent arrow' },
          { id: 'triangle', name: 'Plain triangle' },
          { id: 'sail', name: 'Sailing yacht' },
          { id: 'motor', name: 'Motor cruiser' },
          { id: 'power', name: 'Powerboat' },
        ] as const).map((o) => (
          <button
            key={o.id}
            type="button"
            className={ownshipIcon === o.id ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              set({ ownshipIcon: o.id });
            }}
          >
            {o.name}
          </button>
        ))}
      </div>
      <p className="acp-menu-note">
        Whichever we pick, it points the way the boat is actually facing: the heading when we have a compass,
        otherwise our course over the ground while we are moving.
      </p>

      <div className="rgn">Vectors</div>
      <span className="lead">
        Our own boat&rsquo;s vectors — the lines reaching off the bow that show where we&rsquo;ll be. First, how
        far ahead they reach, in minutes, so a longer time draws a longer line and our speed shows up sooner. It
        mirrors the AIS target vector setting.
      </span>
      <label className="acp-fieldlabel" style={{ marginTop: 8 }}>Ground vector colour</label>
      <div className="acp-stepper" role="radiogroup" aria-label="Ground vector colour">
        {([
          { id: null, name: 'Theme', swatch: null },
          { id: '#e8552b', name: 'Orange', swatch: '#e8552b' },
          { id: '#1f9d6b', name: 'Green', swatch: '#1f9d6b' },
          { id: '#125fc4', name: 'Blue', swatch: '#125fc4' },
          { id: '#111111', name: 'Black', swatch: '#111111' },
          { id: '#ffffff', name: 'White', swatch: '#ffffff' },
        ] as const).map((c) => (
          <button
            key={c.name}
            type="button"
            className={`acp-btn${ownVectorColour === c.id ? ' on' : ''}`}
            aria-label={`Ground vector ${c.name.toLowerCase()}`}
            onClick={() => { haptic('tick'); set({ ownVectorColour: c.id }); }}
            style={c.swatch ? { borderBottom: `3px solid ${c.swatch}` } : undefined}
          >
            {c.name}
          </button>
        ))}
      </div>
      <p className="acp-menu-note">
        Theme follows the palette, so the arrow recolours with day and night. A fixed colour stays put, which is
        the point of picking one: the same arrow on every palette. Heading, wind and tide keep their own colours,
        so the four vectors stay tellable apart.
      </p>

      <div className="rgn">Wind colours</div>
      <span className="lead">
        What counts as a green day depends on the boat. These slide the whole wind colour ladder: green begins at
        the first figure, red at the second, and every band between scales with them. The chart arrows, the wash,
        the scale and the forecast table all move together, so a colour keeps meaning one thing everywhere.
      </span>
      <div className="acp-layer">
        <div className="lt"><b>Green from</b><span>{windGreenFromKn} kn</span></div>
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Green starts a knot lower" onClick={() => set({ windGreenFromKn: Math.max(4, windGreenFromKn - 1) })}>&minus;1</button>
          <button type="button" className="acp-btn" aria-label="Green starts a knot higher" onClick={() => set({ windGreenFromKn: Math.min(windRedFromKn - 6, windGreenFromKn + 1) })}>+1</button>
        </span>
      </div>
      <div className="acp-layer">
        <div className="lt"><b>Red from</b><span>{windRedFromKn} kn</span></div>
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Red starts a knot lower" onClick={() => set({ windRedFromKn: Math.max(windGreenFromKn + 6, windRedFromKn - 1) })}>&minus;1</button>
          <button type="button" className="acp-btn" aria-label="Red starts a knot higher" onClick={() => set({ windRedFromKn: Math.min(48, windRedFromKn + 1) })}>+1</button>
        </span>
      </div>
      {(windGreenFromKn !== 11 || windRedFromKn !== 34) && (
        <button
          type="button"
          className="acp-btn full"
          onClick={() => { haptic('tick'); set({ windGreenFromKn: 11, windRedFromKn: 34 }); }}
        >
          Back to the Beaufort boundaries
        </button>
      )}
      <p className="acp-menu-note">
        The defaults are the Beaufort boundaries (green from 11 kn, top of F3; red from 34 kn, gale). Moving them
        changes where the colours sit, never what the wind is doing, so the numbers in every readout stay the
        forecast&rsquo;s own.
      </p>
      <p className="acp-menu-note">
        6 minutes is the classic RYA one, a tenth of an hour, so the vector reaches our SOG divided by ten in
        miles (6 knots draws 0.6 nm). 30 and 60 give us a longer look down the track, and Custom lets us set any
        value we like.
      </p>
      <div className="picks">
        {presets.map((m) => (
          <button
            key={m}
            type="button"
            className={!customVector && ownVectorMin === m ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              setCustomVector(false);
              set({ ownVectorMin: m });
            }}
          >
            <b>{m} min</b>
          </button>
        ))}
        <button
          key="custom"
          type="button"
          className={customVector ? 'on' : ''}
          onClick={() => {
            haptic('tick');
            setCustomVector(true);
          }}
        >
          <b>Custom</b>
        </button>
      </div>
      {customVector && (
        <NumField
          label="Custom vector length (minutes)"
          value={ownVectorMin}
          min={1}
          hint="How many minutes ahead our own course and heading lines reach. The COG line grows with speed over ground, so a faster boat draws a longer line for the same time."
          onCommit={(v) => {
            if (v != null && v > 0) set({ ownVectorMin: v });
          }}
        />
      )}

      <span className="lead" style={{ marginTop: 12 }}>
        Which vectors show. Course and speed come from the fix; heading, wind and tide each need their own
        source. Turn one on without a source and we&rsquo;ll point you at where to connect it.
      </span>
      {rows.map((r) => (
        <div key={r.id}>
          <ToggleRow
            title={r.title}
            detail={r.detail}
            on={r.on}
            onClick={() => {
              haptic('tick');
              r.setOn(!r.on);
            }}
          />
          {r.on && !available[r.id] && (
            <div className="acp-menu-note" style={{ marginTop: 4 }}>
              <p style={{ margin: '0 0 6px', color: '#e0a03a' }}>{r.missing}</p>
              <button
                type="button"
                className="acp-btn"
                onClick={() => {
                  haptic('tick');
                  openMenuAt(r.group, r.leaf);
                }}
              >
                Take me to {r.where} &rsaquo;
              </button>
            </div>
          )}
        </div>
      ))}

      <span className="lead" style={{ marginTop: 12 }}>
        The EBL and VRM are the other two lines off the boat: the EBL is a bearing line we swing round,
        the VRM a single range circle we grow or shrink. Turn them on or off here, and set the bearing and
        the range right below — the same controls as the EBL/VRM sheet, so there&rsquo;s no need to leave the
        vectors menu. Hold a row to open the full sheet if you&rsquo;d rather.
      </span>
      <ToggleRow
        title="EBL (bearing line)"
        detail="A bearing line from the boat we swing to read off a direction"
        on={eblOn}
        onClick={() => {
          haptic('tick');
          setEblOn(!eblOn);
        }}
        onLongPress={() => openSheet('eblvrm')}
      />
      {eblOn && (
        <>
          <div className="acp-layer">
            <div className="lt">
              <b>EBL bearing</b>
              <span>{formatBearing(eblBearingDeg)}</span>
            </div>
            <input
              className="acp-range"
              style={{ flex: 1, minWidth: 0, maxWidth: 170 }}
              type="range"
              min={0}
              max={359}
              step={1}
              value={Math.round(eblBearingDeg)}
              aria-label="EBL bearing"
              onChange={(e) => setEblBearingDeg(Number(e.target.value))}
            />
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" aria-label="EBL minus 10 degrees" onClick={() => { haptic('tick'); nudgeEbl(-10); }}>&minus;10&deg;</button>
            <button type="button" className="acp-btn" aria-label="EBL minus 1 degree" onClick={() => { haptic('tick'); nudgeEbl(-1); }}>&minus;1&deg;</button>
            <button type="button" className="acp-btn" aria-label="EBL plus 1 degree" onClick={() => { haptic('tick'); nudgeEbl(1); }}>+1&deg;</button>
            <button type="button" className="acp-btn" aria-label="EBL plus 10 degrees" onClick={() => { haptic('tick'); nudgeEbl(10); }}>+10&deg;</button>
          </div>
        </>
      )}
      <ToggleRow
        title="VRM (range circle)"
        detail="A single range circle from the boat we set to a measured distance"
        on={vrmOn}
        onClick={() => {
          haptic('tick');
          setVrmOn(!vrmOn);
        }}
        onLongPress={() => openSheet('eblvrm')}
      />
      {vrmOn && (
        <>
          <span className="acp-fieldlabel" style={{ marginTop: 8 }}>
            VRM range &mdash; {formatDistance(vrmRangeNm)}
          </span>
          <div className="picks">
            {vrmRanges.map((v) => (
              <button
                key={v}
                type="button"
                className={Math.abs(vrmRangeNm - v) < 1e-6 ? 'on' : ''}
                onClick={() => {
                  haptic('tick');
                  setVrmRangeNm(v);
                }}
              >
                <b>{v} nm</b>
              </button>
            ))}
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" aria-label="VRM minus a tenth" onClick={() => { haptic('tick'); nudgeVrm(-0.1); }}>&minus;0.1</button>
            <button type="button" className="acp-btn" aria-label="VRM minus a hundredth" onClick={() => { haptic('tick'); nudgeVrm(-0.01); }}>&minus;0.01</button>
            <button type="button" className="acp-btn" aria-label="VRM plus a hundredth" onClick={() => { haptic('tick'); nudgeVrm(0.01); }}>+0.01</button>
            <button type="button" className="acp-btn" aria-label="VRM plus a tenth" onClick={() => { haptic('tick'); nudgeVrm(0.1); }}>+0.1</button>
          </div>
        </>
      )}

      <span className="lead" style={{ marginTop: 12 }}>
        Range rings are the fixed distance circles centred on the boat, handy for judging how far off
        something is. Turn them on or off here; hold the row for the interval, count and spokes.
      </span>
      <ToggleRow
        title="Range rings"
        detail="Concentric distance rings centred on the boat"
        on={ringsEnabled}
        onClick={() => {
          haptic('tick');
          setRingsEnabled(!ringsEnabled);
        }}
        onLongPress={() => openMenuAt('layers', 'range-rings')}
      />
    </div>
  );
}

function BoatPolarControl() {
  const polarEnabled = useSettingsStore((s) => s.polarEnabled);
  const set = useSettingsStore((s) => s.set);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        A sailing polar tells the auto-router how fast the boat goes at each wind angle, so it can sail and
        tack upwind for the best VMG (velocity made good towards the mark) instead of motoring straight into it.
      </span>
      <ToggleRow
        title="Use a sailing polar"
        detail="On: auto-routing tacks upwind. Off: motor at cruising speed."
        on={polarEnabled}
        onClick={() => set({ polarEnabled: !polarEnabled })}
      />
      <p className="acp-menu-note">
        A typical cruising-yacht polar is built in for now. Editing the curve for our own boat is coming soon.
      </p>
    </div>
  );
}

function AisConfigControl() {
  const enabled = useAisStore((s) => s.enabled);
  const setEnabled = useAisStore((s) => s.setEnabled);
  const source = useAisStore((s) => s.source);
  const setSource = useAisStore((s) => s.setSource);
  const vectorMin = useAisStore((s) => s.vectorMin);
  const setVectorMin = useAisStore((s) => s.setVectorMin);
  const connState = useAisStore((s) => s.connState);
  const connMsg = useAisStore((s) => s.connMsg);
  const targetCount = useAisStore((s) => Object.keys(s.targets).length);
  const apiKey = useAisStore((s) => s.apiKey);
  const setApiKey = useAisStore((s) => s.setApiKey);
  const showToast = useShellStore((s) => s.showToast);
  // Local buffer for the AISStream key so typing is smooth; we commit the
  // trimmed value to the store (and localStorage) on blur/Clear. Hidden by
  // default because the key is a secret — the eye toggle reveals it.
  const [keyText, setKeyText] = useState(apiKey);
  const [showKey, setShowKey] = useState(false);
  useEffect(() => setKeyText(apiKey), [apiKey]);
  const commitKey = () => {
    const trimmed = keyText.trim();
    if (trimmed !== keyText) setKeyText(trimmed);
    if (trimmed !== apiKey) setApiKey(trimmed);
  };
  // Off is a display toggle (enabled=false); the other three choose the feed.
  const sources: { id: 'off' | 'sim' | 'internet' | 'nmea'; name: string; blurb: string; disabled?: boolean }[] = [
    { id: 'off', name: 'Off', blurb: 'no AIS targets shown' },
    { id: 'sim', name: 'Simulated', blurb: 'training vessels over the practice area' },
    { id: 'internet', name: 'Online', blurb: 'live internet AIS, real ships only' },
    { id: 'nmea', name: 'NMEA', blurb: 'needs a connected receiver (coming soon)', disabled: true },
  ];
  const vectors = [6, 30, 60];
  // "Custom" lets us type any minutes value. It shows as active whenever the
  // stored vector isn't one of the presets, or when the user taps Custom.
  const [customVector, setCustomVector] = useState(!vectors.includes(vectorMin));
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        How AIS targets are fed and drawn. Off hides them; the same on/off toggle also lives in Layers &rsquo;
        Overlays. This is where the source and vector length are set.
      </span>
      <span className="lead" style={{ marginTop: 8 }}>Source</span>
      <div className="picks">
        {sources.map((o) => (
          <button
            key={o.id}
            type="button"
            className={(o.id === 'off' ? !enabled : enabled && source === o.id) ? 'on' : ''}
            disabled={o.disabled}
            title={o.disabled ? 'No NMEA receiver connection is available in this build yet.' : undefined}
            style={o.disabled ? { opacity: 0.5 } : undefined}
            onClick={() => {
              if (o.disabled) return;
              haptic('tick');
              if (o.id === 'off') {
                setEnabled(false);
                showToast('AIS: Off');
              } else {
                setSource(o.id);
                setEnabled(true);
                showToast(`AIS source: ${o.name}`);
              }
            }}
          >
            <b>{o.name}</b>
            <span>{o.blurb}</span>
          </button>
        ))}
      </div>
      {enabled && source === 'internet' && (
        <div className="acp-layer" style={{ marginTop: 8 }}>
          <div className="lt">
            <b>Feed status</b>
            <span>{aisStatusLabel(connState, targetCount, connMsg)}</span>
          </div>
        </div>
      )}
      {enabled && source === 'internet' && (
        <>
          <label className="acp-fieldlabel" htmlFor="ais-key-input">AISStream API key</label>
          <input
            id="ais-key-input"
            className="acp-text-input"
            type={showKey ? 'text' : 'password'}
            value={keyText}
            placeholder="Paste your key"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-label="AISStream API key"
            onChange={(e) => setKeyText(e.target.value)}
            onBlur={commitKey}
          />
          <div className="picks" style={{ marginTop: 6 }}>
            <button
              type="button"
              aria-pressed={showKey}
              onClick={() => { haptic('tick'); setShowKey((v) => !v); }}
            >
              <b>{showKey ? 'Hide' : 'Show'}</b>
            </button>
            <button
              type="button"
              disabled={keyText === '' && apiKey === ''}
              onClick={() => { haptic('tick'); setKeyText(''); setApiKey(''); }}
            >
              <b>Clear</b>
            </button>
          </div>
          <p className="acp-menu-note" style={{ marginTop: 2 }}>
            Free key from aisstream.io &mdash; a key entered here overrides the built-in one.
          </p>
        </>
      )}
      <span className="lead" style={{ marginTop: 8 }}>
        Course/speed vector — how far ahead each target&rsquo;s line reaches, in minutes. It mirrors our
        own-ship vector setting.
      </span>
      <p className="acp-menu-note">
        6 minutes is the classic RYA one, a tenth of an hour, so a target&rsquo;s line reaches its speed
        divided by ten in miles (a 10-knot ship draws 1 nm). 30 and 60 give a longer look at where the
        traffic is heading, and Custom lets us set any value we like.
      </p>
      <div className="picks">
        {vectors.map((m) => (
          <button
            key={m}
            type="button"
            className={!customVector && vectorMin === m ? 'on' : ''}
            onClick={() => {
              haptic('tick');
              setCustomVector(false);
              setVectorMin(m);
            }}
          >
            <b>{m} min</b>
          </button>
        ))}
        <button
          key="custom"
          type="button"
          className={customVector ? 'on' : ''}
          onClick={() => {
            haptic('tick');
            setCustomVector(true);
          }}
        >
          <b>Custom</b>
        </button>
      </div>
      {customVector && (
        <NumField
          label="Custom vector length (minutes)"
          value={vectorMin}
          min={1}
          hint="How many minutes ahead each target's course line reaches. A longer time draws a longer line, so fast ships stand out sooner."
          onCommit={(v) => {
            if (v != null && v > 0) setVectorMin(v);
          }}
        />
      )}
    </div>
  );
}

function AnchorWatchControl() {
  const anchor = useAnchorWatchStore((s) => s.anchor);
  const scopeM = useAnchorWatchStore((s) => s.scopeM);
  const setScopeM = useAnchorWatchStore((s) => s.setScopeM);
  const depthM = useAnchorWatchStore((s) => s.depthM);
  const setDepthM = useAnchorWatchStore((s) => s.setDepthM);
  const weighAnchor = useAnchorWatchStore((s) => s.weighAnchor);
  const showToast = useShellStore((s) => s.showToast);
  const anchorDropArmed = useShellStore((s) => s.anchorDropArmed);
  const setAnchorDropArmed = useShellStore((s) => s.setAnchorDropArmed);
  const anchored = anchor != null;
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Drops a watch circle at the anchor and warns if we drag out of it. Scope is the length of rode
        we&rsquo;ve let out; more scope means a bigger swing circle.
      </span>
      <div className="acp-layer">
        <div className="lt">
          <b>Status</b>
          <span>{anchored ? 'Anchored — watch active' : 'Not anchored'}</span>
        </div>
        {/* Drop here used to live on this row. It now sits in AnchorPlacement
            below alongside the other two ways of placing the anchor, so the
            three are read together rather than one being the obvious button and
            the others buried further down. Weigh stays here: it belongs with the
            status it changes, and it is not a placement. */}
        {anchored && (
          <button type="button" className="acp-btn" onClick={() => { haptic('commit'); weighAnchor(); showToast('Anchor weighed'); }}>Weigh</button>
        )}
      </div>
      {/* Ready anchor: puts a one-tap Drop-anchor button on the chart HUD so the
          hook can be let go the moment it hits the bottom without coming back to
          this menu. The button drops at the boat and arms the watch, then hides
          itself once the anchor's down. */}
      <ToggleRow
        title="Ready anchor"
        detail={anchorDropArmed ? 'Drop button on the chart' : 'Off'}
        on={anchorDropArmed}
        onClick={() => setAnchorDropArmed(!anchorDropArmed)}
      />
      <div className="acp-layer">
        <div className="lt"><b>Scope (rode out)</b><span>{scopeM} m</span></div>
        {/* Coarse (±10) alongside fine (±1), the same layout as the depth stepper
            below so the two read consistently, so paying out a long scope isn't a
            run of small taps while the ±1 stays for a precise trim (fb-ms529by4-7).
            Clamped to a minimum of 1 m — no rode out is no anchor set. No max, as
            before. */}
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Much less scope, 10 metres" onClick={() => setScopeM(Math.max(1, scopeM - 10))}>&minus;10</button>
          <button type="button" className="acp-btn" aria-label="Less scope, 1 metre" onClick={() => setScopeM(Math.max(1, scopeM - 1))}>&minus;1</button>
          <button type="button" className="acp-btn" aria-label="More scope, 1 metre" onClick={() => setScopeM(scopeM + 1)}>+1</button>
          <button type="button" className="acp-btn" aria-label="Much more scope, 10 metres" onClick={() => setScopeM(scopeM + 10)}>+10</button>
        </span>
      </div>
      <div className="acp-layer">
        <div className="lt"><b>Depth of water</b><span>{depthM} m</span></div>
        {/* Coarse (±10) alongside fine (±1) so setting a deep anchorage isn't a
            long run of single-metre taps, while the ±1 stays for a precise trim
            (fb-ms529by4-7). Clamped to a minimum of 1 m — a zero or negative
            depth of water is meaningless for sizing the swing circle. No max, as
            before. */}
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Much shallower, 10 metres" onClick={() => setDepthM(Math.max(1, depthM - 10))}>&minus;10</button>
          <button type="button" className="acp-btn" aria-label="Shallower, 1 metre" onClick={() => setDepthM(Math.max(1, depthM - 1))}>&minus;1</button>
          <button type="button" className="acp-btn" aria-label="Deeper, 1 metre" onClick={() => setDepthM(depthM + 1)}>+1</button>
          <button type="button" className="acp-btn" aria-label="Much deeper, 10 metres" onClick={() => setDepthM(depthM + 10)}>+10</button>
        </span>
      </div>
      <AnchorPlacement />
      <AnchorBoundaryShape />
      <p className="acp-menu-note">
        The drag alarm raises on the same banner as the other alarms. Anchor can also be dropped by long-pressing open water on the chart.
      </p>
    </div>
  );
}

function GroundingAlarmControl() {
  const config = useAlarmsStore((s) => s.config);
  const setConfig = useAlarmsStore((s) => s.setConfig);
  const safetyDepthM = useSettingsStore((s) => s.safetyDepthM);
  // The danger cone and its reach live in the safety store, the same fields the
  // Alarms panel writes, so there is one source of truth for the look-ahead. This
  // control is also where the "Danger cone & look-ahead" shortcut (boat popover)
  // lands, so the cone on/off and its distance/time reach have to be here, not
  // just the grounding switch.
  const coneOn = useSafetyStore((s) => s.settings.coneOn);
  const lookaheadMode = useSafetyStore((s) => s.settings.lookaheadMode);
  const lookaheadMin = useSafetyStore((s) => s.settings.lookaheadMin);
  const lookaheadNm = useSafetyStore((s) => s.settings.lookaheadNm);
  const setSafety = useSafetyStore((s) => s.setSettings);
  // The look-ahead TIME is offered as the usual 6 / 30 / 60 minute chips, with
  // Custom revealing the free minutes field (feedback: "can we make the options
  // 6, 30, 60 mins and custom"). The stored value stays lookaheadMin in minutes,
  // so this only changes the input, not the units or the reach maths.
  const lookaheadPresets = [6, 30, 60];
  const [customLookahead, setCustomLookahead] = useState(!lookaheadPresets.includes(lookaheadMin));
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        The grounding alarm looks ahead. It projects our track forward at the course and speed we&rsquo;re
        doing now and warns if that line would run into water shallower than the safety depth, telling us
        how long until we&rsquo;d get there. So we hear about the shoal while there&rsquo;s still room to turn, not
        once we&rsquo;re sitting in it. Where the depth alarm reads the water under us this moment, this one
        looks down our track into the future.
      </span>
      <ToggleRow
        title="Grounding / safety-contour alarm"
        detail={`Projects our track ahead and warns if it would reach water shallower than the safety depth (now ${safetyDepthM.toFixed(0)} m)`}
        on={config.groundingEnabled}
        onClick={() => setConfig({ groundingEnabled: !config.groundingEnabled })}
      />
      <ToggleRow
        title="Danger cone"
        detail="Draws a cone ahead of the boat and flags wrecks, obstructions and shallow water inside it. The same reach sets how far the grounding look-ahead projects."
        on={coneOn}
        onClick={() => setSafety({ coneOn: !coneOn })}
      />
      {coneOn && (
        <>
          <div className="picks">
            <button
              type="button"
              className={lookaheadMode === 'distance' ? 'on' : ''}
              onClick={() => setSafety({ lookaheadMode: 'distance' })}
            >
              <b>By distance</b>
            </button>
            <button
              type="button"
              className={lookaheadMode === 'time' ? 'on' : ''}
              onClick={() => setSafety({ lookaheadMode: 'time' })}
            >
              <b>By time</b>
            </button>
          </div>
          {lookaheadMode === 'distance' ? (
            <NumField
              label="Look ahead (nm)"
              value={lookaheadNm}
              min={0}
              placeholder="0.5"
              hint="A fixed distance ahead of the boat, whatever our speed."
              onCommit={(v) => {
                if (v != null && v > 0) setSafety({ lookaheadNm: v });
              }}
            />
          ) : (
            <>
              <span className="lead" style={{ marginTop: 8 }}>Look ahead (minutes at current speed)</span>
              <p className="acp-menu-note">
                Reach is speed x minutes, so it grows with our speed. Near a stop it holds a short fixed
                reach so a danger right ahead still shows.
              </p>
              <div className="picks">
                {lookaheadPresets.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={!customLookahead && lookaheadMin === m ? 'on' : ''}
                    onClick={() => {
                      haptic('tick');
                      setCustomLookahead(false);
                      setSafety({ lookaheadMin: m });
                    }}
                  >
                    <b>{m} min</b>
                  </button>
                ))}
                <button
                  key="custom"
                  type="button"
                  className={customLookahead ? 'on' : ''}
                  onClick={() => {
                    haptic('tick');
                    setCustomLookahead(true);
                  }}
                >
                  <b>Custom</b>
                </button>
              </div>
              {customLookahead && (
                <NumField
                  label="Custom look-ahead (minutes at current speed)"
                  value={lookaheadMin}
                  min={0}
                  placeholder="6"
                  hint="How many minutes ahead the look-ahead projects at our current speed."
                  onCommit={(v) => {
                    if (v != null && v > 0) setSafety({ lookaheadMin: v });
                  }}
                />
              )}
            </>
          )}
        </>
      )}
      <p className="acp-menu-note">Set the safety depth under Safety &rsquo; Safety depth.</p>
    </div>
  );
}

function DepthAlarmControl() {
  const config = useAlarmsStore((s) => s.config);
  const setConfig = useAlarmsStore((s) => s.setConfig);
  const step = (d: number) => setConfig({ depthLimitM: Math.max(0, Math.round((config.depthLimitM + d) * 10) / 10) });
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        The depth alarm reads the water under us right now, not ahead. It works out the clearance beneath
        the keel where we&rsquo;re floating this moment (the charted depth, plus the height of tide now, less our
        draught) and warns when that drops below the clearance we set here. So this is the here-and-now
        reading, and the grounding alarm is the look-ahead.
      </span>
      <ToggleRow
        title="Depth / sounder alarm"
        detail="Warns when the clearance under the keel right now falls below the limit we set"
        on={config.depthEnabled}
        onClick={() => setConfig({ depthEnabled: !config.depthEnabled })}
      />
      <div className="acp-layer">
        <div className="lt"><b>Clearance limit</b><span>{config.depthLimitM.toFixed(1)} m</span></div>
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Less clearance" onClick={() => step(-0.5)}>&minus;</button>
          <button type="button" className="acp-btn" aria-label="More clearance" onClick={() => step(0.5)}>+</button>
        </span>
      </div>
    </div>
  );
}

function ArrivalAlarmControl() {
  const config = useAlarmsStore((s) => s.config);
  const setConfig = useAlarmsStore((s) => s.setConfig);
  const step = (d: number) => setConfig({ proximityNm: Math.max(0, Math.round((config.proximityNm + d) * 100) / 100) });
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Warns as we come within a set distance of the active waypoint, so an arrival or a turn doesn&rsquo;t
        sneak up on us.
      </span>
      <ToggleRow
        title="Arrival / proximity alarm"
        detail="On when nearing the active waypoint"
        on={config.proximityEnabled}
        onClick={() => setConfig({ proximityEnabled: !config.proximityEnabled })}
      />
      <div className="acp-layer">
        <div className="lt"><b>Arrival radius</b><span>{config.proximityNm.toFixed(2)} nm</span></div>
        <span className="acp-stepper">
          <button type="button" className="acp-btn" aria-label="Smaller radius" onClick={() => step(-0.05)}>&minus;</button>
          <button type="button" className="acp-btn" aria-label="Larger radius" onClick={() => step(0.05)}>+</button>
        </span>
      </div>
    </div>
  );
}

function TideHeightAlarmControl() {
  const config = useAlarmsStore((s) => s.config);
  const setConfig = useAlarmsStore((s) => s.setConfig);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Warns when the predicted height of tide now crosses a level we set, too low to float or clear a
        sill, or too high to fit under a bridge. It reads the same tide prediction the tide card shows, so
        the alarm and the curve always agree.
      </span>
      <ToggleRow
        title="Tide-height alarm"
        detail="Warns when the height of tide now crosses the limit we set"
        on={config.tideHeightEnabled}
        onClick={() => setConfig({ tideHeightEnabled: !config.tideHeightEnabled })}
      />
      <span className="lead" style={{ marginTop: 8 }}>Warn when the height goes&hellip;</span>
      <div className="picks">
        <button
          type="button"
          className={config.tideHeightMode === 'below' ? 'on' : ''}
          onClick={() => { haptic('tick'); setConfig({ tideHeightMode: 'below' }); }}
        >
          <b>Below the limit</b>
        </button>
        <button
          type="button"
          className={config.tideHeightMode === 'above' ? 'on' : ''}
          onClick={() => { haptic('tick'); setConfig({ tideHeightMode: 'above' }); }}
        >
          <b>Above the limit</b>
        </button>
      </div>
      <NumField
        label="Height limit (m above chart datum)"
        value={config.tideHeightLimitM}
        min={0}
        placeholder="1"
        hint="Heights are to chart datum, the same zero the tide card and the chart soundings use."
        onCommit={(v) => {
          if (v != null && v >= 0) setConfig({ tideHeightLimitM: v });
        }}
      />
      <p className="acp-menu-note">The full set of alarms and their thresholds live under Safety &rsquo; Alarms.</p>
    </div>
  );
}

// `hideAirDraught` is set when this control sits inside the Boat profile, which
// already carries its own Air draught field just above. We'd otherwise draw a
// second field bound to the same store value, so in that spot we drop our own
// input and keep just the explanation. In the Safety menu (default) the field
// stays, since that's the only place bridge clearance offers to set it.
function BridgeClearanceControl({ hideAirDraught = false }: { hideAirDraught?: boolean }) {
  const airDraughtM = useSettingsStore((s) => s.airDraughtM);
  const set = useSettingsStore((s) => s.set);
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Bridge clearance is checked against our air draught, the height of our highest fixed point above the
        water. Tap a bridge on the chart and we&rsquo;ll work out whether we fit under at the current tide.
      </span>
      {!hideAirDraught && (
        <NumField
          label="Air draught (m)"
          value={airDraughtM}
          allowEmpty
          placeholder="e.g. 14"
          onCommit={(v) => set({ airDraughtM: v })}
          hint="Leave blank if we're not sure; then we can't check clearance."
        />
      )}
      <p className="acp-menu-note">
        {hideAirDraught
          ? 'Uses the air draught set above — one figure, used everywhere.'
          : 'Same figure as in Boat profile — set once, used everywhere.'}
      </p>
    </div>
  );
}

function MarksControl() {
  const marks = useRoutingStore((s) => s.marks);
  const removeMark = useRoutingStore((s) => s.removeMark);
  const gotoMark = useRoutingStore((s) => s.gotoMark);
  const ownPos = useChartPlotterStore((s) => s.ownship.position);
  const showToast = useShellStore((s) => s.showToast);
  const closeSheet = useShellStore((s) => s.closeSheet);
  if (!marks.length) {
    return (
      <div className="acp-chartsrc">
        <span className="lead">Your saved marks, the spots we&rsquo;ve dropped on the chart to come back to.</span>
        <EmptyHint>{EMPTY_MARKS}</EmptyHint>
      </div>
    );
  }
  return (
    <div className="acp-chartsrc">
      <span className="lead">Our saved marks. Go to one to steer for it, or delete the ones we&rsquo;re done with.</span>
      {marks.map((m) => (
        <div className="acp-layer" key={m.id}>
          <div className="lt">
            <b>{m.name || 'Mark'}</b>
            <span>{formatPosition(m.position)}</span>
          </div>
          <span className="acp-stepper">
            <button
              type="button"
              className="acp-btn"
              disabled={!ownPos}
              onClick={() => {
                if (!ownPos) return;
                haptic('tick');
                gotoMark(m.id, ownPos);
                showToast(`Going to ${m.name || 'mark'}`);
                closeSheet();
              }}
            >
              Go to
            </button>
            <button
              type="button"
              className="acp-btn"
              aria-label={`Delete ${m.name || 'mark'}`}
              onClick={() => { haptic('commit'); removeMark(m.id); showToast('Mark deleted'); }}
            >
              ✕
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Notes & annotations hub. The one home for the skipper's own notes and freehand
 * marks on the chart: a list of every note dropped (fly to one, edit it, or
 * delete it), a New note button that lets us choose where the next one goes, and
 * a freehand sketch tool for drawing on the chart (bearings, clearing lines, a
 * ring round a hazard). The freehand drawing reuses the pilotage-plan sketch
 * tool, so an annotation is saved inside a pilotage plan rather than lost.
 */
function NotesControl() {
  const notes = useNotesStore((s) => s.notes);
  const removeNote = useNotesStore((s) => s.removeNote);
  const setEditingNote = useShellStore((s) => s.setEditingNote);
  const armPlaceNote = useShellStore((s) => s.armPlaceNote);
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const setFocus = useShellStore((s) => s.setFocus);
  const showToast = useShellStore((s) => s.showToast);
  const newPlan = usePilotagePlannerStore((s) => s.newPlan);
  const setTool = usePilotagePlannerStore((s) => s.setTool);

  function newNote() {
    // No location yet, so arm place-note mode: the next chart tap drops the note
    // where we want it. Closing the menu clears the way to tap.
    haptic('tick');
    armPlaceNote();
    showToast('Tap the chart where the note should go');
  }

  function drawAnnotation() {
    // Freehand sketching lives in the pilotage-plan tool, so start a fresh plan
    // and arm the freehand tool. The drawing is then saved inside that plan.
    haptic('tick');
    newPlan();
    setTool('freehand');
    closeSheet();
    showToast('Press and drag on the chart to sketch');
  }

  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Our own notes and marks on the chart. Drop a post-it to come back to a spot, or sketch
        freehand for a clearing line or a ring round a hazard.
      </span>
      <div className="acp-btn-row" style={{ marginTop: 8 }}>
        <button type="button" className="acp-btn primary" onClick={newNote}>
          New note
        </button>
        <button type="button" className="acp-btn" onClick={drawAnnotation}>
          Draw freehand
        </button>
      </div>
      {notes.length === 0 ? (
        <p className="acp-menu-note">
          No notes yet. Tap New note, then tap the chart where it should go, or drop one from a
          tapped point or a charted object.
        </p>
      ) : (
        <>
          <span className="lead" style={{ marginTop: 10 }}>Saved notes</span>
          {notes.map((n) => (
            <div className="acp-layer" key={n.id}>
              <div className="lt">
                <b>{n.title || (n.text ? n.text.slice(0, 24) : 'Note')}</b>
                <span>{formatPosition(n.position)}</span>
              </div>
              <span className="acp-stepper">
                <button
                  type="button"
                  className="acp-btn"
                  onClick={() => {
                    haptic('tick');
                    setFocus(n.position, 14);
                    setEditingNote(n.id);
                    openSheet('note');
                  }}
                >
                  Open
                </button>
                <button
                  type="button"
                  className="acp-btn"
                  aria-label={`Delete ${n.title || 'note'}`}
                  onClick={() => { haptic('commit'); removeNote(n.id); showToast('Note deleted'); }}
                >
                  ✕
                </button>
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function UnitsControl() {
  const depth = useUnitsStore((s) => s.depth);
  const distance = useUnitsStore((s) => s.distance);
  const speed = useUnitsStore((s) => s.speed);
  const set = useUnitsStore((s) => s.set);
  const showToast = useShellStore((s) => s.showToast);
  const depths: { id: DepthUnit; name: string }[] = [
    { id: 'm', name: 'Metres' },
    { id: 'ft', name: 'Feet' },
  ];
  const distances: { id: DistanceUnit; name: string }[] = [
    { id: 'nm', name: 'Nautical miles' },
    { id: 'km', name: 'Kilometres' },
  ];
  const speeds: { id: SpeedUnit; name: string }[] = [
    { id: 'kn', name: 'Knots' },
    { id: 'kmh', name: 'km/h' },
  ];
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        The units the app shows. Everything is worked out in the background the same way; this only
        changes how the numbers read.
      </span>
      <span className="lead" style={{ marginTop: 8 }}>Depth &amp; height</span>
      <div className="picks">
        {depths.map((o) => (
          <button key={o.id} type="button" className={depth === o.id ? 'on' : ''}
            onClick={() => { haptic('tick'); set({ depth: o.id }); showToast(`Depth in ${o.name.toLowerCase()}`); }}>
            <b>{o.name}</b>
          </button>
        ))}
      </div>
      <span className="lead" style={{ marginTop: 8 }}>Distance</span>
      <div className="picks">
        {distances.map((o) => (
          <button key={o.id} type="button" className={distance === o.id ? 'on' : ''}
            onClick={() => { haptic('tick'); set({ distance: o.id }); showToast(`Distance in ${o.name.toLowerCase()}`); }}>
            <b>{o.name}</b>
          </button>
        ))}
      </div>
      <span className="lead" style={{ marginTop: 8 }}>Speed</span>
      <div className="picks">
        {speeds.map((o) => (
          <button key={o.id} type="button" className={speed === o.id ? 'on' : ''}
            onClick={() => { haptic('tick'); set({ speed: o.id }); showToast(`Speed in ${o.name}`); }}>
            <b>{o.name}</b>
          </button>
        ))}
      </div>
    </div>
  );
}

function PositionFormatControl() {
  const position = useUnitsStore((s) => s.position);
  const set = useUnitsStore((s) => s.set);
  const showToast = useShellStore((s) => s.showToast);
  // A sample coordinate so each option shows exactly how a position will read.
  const sampleLat = 50.797205;
  const opts: { id: PositionFormat; name: string; blurb: string }[] = [
    { id: 'dmm', name: 'Deg, min, dec-min', blurb: formatLatLon(sampleLat, 'lat', { format: 'dmm' }) },
    { id: 'dms', name: 'Deg, min, sec', blurb: formatLatLon(sampleLat, 'lat', { format: 'dms' }) },
    { id: 'dd', name: 'Decimal degrees', blurb: formatLatLon(sampleLat, 'lat', { format: 'dd' }) },
  ];
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        How positions read. Degrees, minutes and decimal minutes is the default, because that&rsquo;s how paper
        charts and RYA courses work. The other two are here for anyone who prefers them.
      </span>
      <div className="picks">
        {opts.map((o) => (
          <button key={o.id} type="button" className={position === o.id ? 'on' : ''}
            onClick={() => { haptic('tick'); set({ position: o.id }); showToast(`Positions in ${o.name.toLowerCase()}`); }}>
            <b>{o.name}</b>
            <span>{o.blurb}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Saved tracks — the on-device library of named own-ship tracks. This is the real
 * "Saved tracks" screen (User data): save the track we're on now, show or hide
 * saved ones on the chart, export one as GPX, or delete the ones we're done with.
 * It drives the savedTracks store, the same one the boat popover's "Save track"
 * writes to, so there's one home for saved tracks.
 */
function downloadGpxFile(xml: string, filename: string): void {
  const blob = new Blob([xml], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Give the browser a moment to start the download before releasing the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function SavedTracksControl() {
  const tracks = useSavedTracksStore((s) => s.tracks);
  const hiddenIds = useSavedTracksStore((s) => s.hiddenIds);
  const removeTrack = useSavedTracksStore((s) => s.removeTrack);
  const toggleHidden = useSavedTracksStore((s) => s.toggleHidden);
  const saveTrack = useSavedTracksStore((s) => s.saveTrack);
  // Select the track object (a stable reference) and derive the points, rather
  // than selecting a fresh array each render, which would loop the store.
  const primaryTrack = useChartPlotterStore((s) => s.tracks[PRIMARY_TRACK_ID]);
  const showToast = useShellStore((s) => s.showToast);

  const saveCurrent = () => {
    haptic('tick');
    const id = saveTrack(primaryTrack?.points ?? []);
    showToast(id ? 'Track saved to the device' : 'Not enough track to save yet');
  };
  const exportGpx = (t: { id: string; name: string; points: TrackPoint[] }) => {
    haptic('tick');
    const xml = exportTrackToGpx({ id: t.id, points: t.points, kind: 'secondary' }, t.name);
    downloadGpxFile(xml, `${t.name.replace(/[^a-z0-9-_]+/gi, '_') || 'track'}.gpx`);
    showToast('GPX exported');
  };
  return (
    <div className="acp-chartsrc">
      <span className="lead">
        Our saved tracks, kept on this device. Save the track we&rsquo;re on now, show or hide the old ones
        on the chart, export one as GPX, or delete the ones we&rsquo;re done with.
      </span>
      <button type="button" className="acp-btn" style={{ marginTop: 6 }} onClick={saveCurrent}>
        Save current track
      </button>
      {tracks.length === 0 ? (
        <EmptyHint>{EMPTY_TRACKS}</EmptyHint>
      ) : (
        tracks.map((t) => {
          const hidden = hiddenIds.includes(t.id);
          return (
            <div className="acp-layer" key={t.id}>
              <div className="lt">
                <b>{t.name}</b>
                <span>
                  {t.points.length} point{t.points.length === 1 ? '' : 's'} &middot;{' '}
                  {new Date(t.savedAt).toLocaleDateString()}
                </span>
              </div>
              <span className="acp-stepper">
                <button
                  type="button"
                  className={`acp-sw${hidden ? '' : ' on'}`}
                  aria-pressed={!hidden}
                  aria-label={`Show ${t.name} on the chart`}
                  onClick={() => { haptic('select'); toggleHidden(t.id); }}
                />
                <button type="button" className="acp-btn" aria-label={`Export ${t.name} as GPX`} onClick={() => exportGpx(t)}>
                  GPX
                </button>
                <button
                  type="button"
                  className="acp-btn"
                  aria-label={`Delete ${t.name}`}
                  onClick={() => { haptic('commit'); removeTrack(t.id); showToast('Track deleted'); }}
                >
                  &times;
                </button>
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}

/** Navigate the menu to another leaf's screen (group id + leaf id). */
export type MenuNavigate = (target: { group: string; leaf: string }) => void;

/** Dispatch a control id to its focused control. Rendered inside the menu. */
export function MenuControl({ control, onNavigate }: { control: string; onNavigate?: MenuNavigate }) {
  switch (control) {
    case 'provider':
      return <ChartProviderPicker />;
    case 'palette':
      return <PaletteControl />;
    case 'haptics':
      return <HapticsControl />;
    case 'alarmsound':
      return <AlarmSoundControl />;
    case 'bggps':
      return <BackgroundGpsControl />;
    case 'glass':
      return <GlassControl />;
    case 'keepawake':
      return <KeepAwakeControl />;
    case 'about':
      return <AboutControl />;
    case 'diagnostics':
      return <DiagnosticsPanel />;
    case 'units':
      return <UnitsControl />;
    case 'posformat':
      return <PositionFormatControl />;
    case 'symbolset':
      return <SymbolSetControl />;
    case 'declutter':
      return <DeclutterControl />;
    case 'depthshading':
      return <DepthShadingControl />;
    case 'orientation':
      return <OrientationControl />;
    case 'overlays':
      return <OverlaysControl onNavigate={onNavigate} />;
    case 'rangerings':
      return <RangeRingsControl />;
    case 'safetydepth':
      return <SafetyDepthControl />;
    case 'gnsssource':
      return <GnssSourceControl />;
    case 'boatprofile':
      return <BoatProfileControl />;
    case 'bearingdisplay':
      return <BearingDisplayControl />;
    case 'ownvector':
      return <OwnShipVectorControl />;
    case 'boatpolar':
      return <BoatPolarControl />;
    case 'aisconfig':
      return <AisConfigControl />;
    case 'anchorwatch':
      return <AnchorWatchControl />;
    case 'grounding':
      return <GroundingAlarmControl />;
    case 'depthalarm':
      return <DepthAlarmControl />;
    case 'arrivalalarm':
      return <ArrivalAlarmControl />;
    case 'tideheight':
      return <TideHeightAlarmControl />;
    case 'bridgeclearance':
      return <BridgeClearanceControl />;
    case 'marks':
      return <MarksControl />;
    case 'notes':
      return <NotesControl />;
    case 'savedtracks':
      return <SavedTracksControl />;
    case 'currency':
      return <CurrencyControl />;
    default:
      return <p className="acp-menu-note">This control isn’t wired up yet.</p>;
  }
}
